/**
 * FeaturesSection Component
 *
 * Displays tier-specific features with icons
 * Features differ between Drama+ and DramaX tiers
 */
import { View, Text } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { useGroupAnalysisFlag } from "../../../hooks/useFeatureFlags";

type Tier = 'drama_plus' | 'dramax';
type PlanType = 'individual' | 'family';

interface Feature {
  icon: string;
  text: string;
}

interface FeaturesSectionProps {
  selectedTier: Tier;
  planType: PlanType;
}

export function FeaturesSection({ selectedTier, planType }: FeaturesSectionProps) {
  const { t } = useTranslation('subscription');
  const groupAnalysisEnabled = useGroupAnalysisFlag();

  // Drama+ features (translated)
  const dramaPlusFeatures: Feature[] = [
    { icon: '✓', text: t('features.drama_plus.analyses') },
    { icon: '📊', text: t('features.drama_plus.patterns') },
  ];

  // DramaX features (translated)
  const dramaxFeatures: Feature[] = [
    { icon: '✓', text: t('features.dramax.analyses') },
    { icon: '📝', text: t('features.dramax.multimodal') },
    { icon: '🎭', text: t('features.dramax.archetypes') },
    { icon: '🔮', text: t('features.dramax.origins') },
    { icon: '🌟', text: t('features.dramax.transformation') },
    { icon: '👥', text: t('features.dramax.practitioner') },
    ...(groupAnalysisEnabled !== false ? [{ icon: '📊', text: t('features.dramax.group') }] : []),
  ];

  const features = selectedTier === 'drama_plus' ? dramaPlusFeatures : dramaxFeatures;

  return (
    <>
      {/* Hero Banner */}
      <View className="mx-4 mb-4 bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-8 items-center">
        <Text className="text-white text-2xl font-bold text-center mb-2">
          {selectedTier === 'drama_plus'
            ? t('hero.drama_plus')
            : t('hero.dramax')}
        </Text>
        <Text className="text-white/90 text-center">
          {planType === 'family' ? t('hero.family') : t('hero.individual')}
        </Text>
      </View>

      {/* Features List */}
      <View className="px-4 mb-6">
        {features.map((feature, index) => (
          <View key={index} className="flex-row items-center mb-4">
            <Text className="text-2xl mr-3">{feature.icon}</Text>
            <Text className="text-gray-700 text-base flex-1">{feature.text}</Text>
          </View>
        ))}
      </View>
    </>
  );
}
